import { useEffect, useState } from "react";
import api, { euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Trash2, Plus, Percent, AlertTriangle } from "lucide-react";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [recipients, setRecipients] = useState("");
  const [alertDay, setAlertDay] = useState("15");
  const [marginThreshold, setMarginThreshold] = useState("");
  const [recipeFcThreshold, setRecipeFcThreshold] = useState("");
  const [recipeRiseThreshold, setRecipeRiseThreshold] = useState("");
  const [priceRiseThreshold, setPriceRiseThreshold] = useState("");
  const [compositeFcThreshold, setCompositeFcThreshold] = useState("");
  const [fuzzyThreshold, setFuzzyThreshold] = useState("");
  const [defaultVat, setDefaultVat] = useState("");

  const load = () => {
    api.get("/sales").then(({ data }) => setSales(data));
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    api.get("/settings").then(({ data }) => { setThreshold(data.food_cost_threshold ?? ""); setRecipients((data.report_recipients || []).join(", ")); setAlertDay(String(data.forecast_alert_day ?? 15)); setMarginThreshold(data.margin_threshold_pct ?? ""); setRecipeFcThreshold(data.recipe_food_cost_threshold ?? ""); setRecipeRiseThreshold(data.recipe_cost_rise_threshold ?? ""); setPriceRiseThreshold(data.price_rise_threshold_pct ?? ""); setCompositeFcThreshold(data.composite_food_cost_threshold ?? ""); setFuzzyThreshold(data.fuzzy_match_threshold ?? ""); setDefaultVat(data.default_vat_rate ?? ""); });
  };
  useEffect(() => { load(); }, []);

  const saveFuzzyThreshold = async () => {
    await api.put("/settings", { fuzzy_match_threshold: parseFloat(fuzzyThreshold) || 0 });
    toast.success("Soglia abbinamento salvata");
    load();
  };

  const saveDefaultVat = async () => {
    await api.put("/settings", { default_vat_rate: parseFloat(defaultVat) || 0 });
    toast.success("IVA di default salvata");
    load();
  };

  const saveMarginThreshold = async () => {
    await api.put("/settings", { margin_threshold_pct: parseFloat(marginThreshold) || 0 });
    toast.success("Soglia margine salvata");
    load();
  };
  const saveRecipeFc = async () => {
    await api.put("/settings", { recipe_food_cost_threshold: parseFloat(recipeFcThreshold) || 0 });
    toast.success("Soglia food cost piatti salvata");
    load();
  };
  const saveRecipeRise = async () => {
    await api.put("/settings", { recipe_cost_rise_threshold: parseFloat(recipeRiseThreshold) || 0 });
    toast.success("Soglia rincaro piatti salvata");
    load();
  };
  const savePriceRise = async () => {
    await api.put("/settings", { price_rise_threshold_pct: parseFloat(priceRiseThreshold) || 0 });
    toast.success("Soglia rincaro acquisti salvata");
    load();
  };
  const saveCompositeFc = async () => {
    await api.put("/settings", { composite_food_cost_threshold: parseFloat(compositeFcThreshold) || 0 });
    toast.success("Soglia food cost composti salvata");
    load();
  };

  const saveThreshold = async () => {
    await api.put("/settings", { food_cost_threshold: parseFloat(threshold) || 0 });
    toast.success("Soglia salvata");
    load();
  };

  const saveRecipients = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    await api.put("/settings", { report_recipients: list });
    toast.success("Destinatari salvati");
    load();
  };

  const saveAlertDay = async () => {
    await api.put("/settings", { forecast_alert_day: parseInt(alertDay) || 15 });
    toast.success("Giorno avviso salvato");
    load();
  };

  const save = async () => {
    if (!month || !amount) { toast.error("Inserisci mese e importo"); return; }
    setSaving(true);
    try {
      await api.post("/sales", { month, amount: parseFloat(amount) });
      toast.success("Vendite salvate");
      setAmount("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/sales/${id}`); load(); };

  const chartData = (stats?.series || []).filter((s) => s.sales > 0 || s.costs > 0);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Ricavi</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Vendite &amp; Incidenza</h1>
        <p className="text-sm text-stone-500 mt-2">Inserisci il fatturato mensile per calcolare l'incidenza dei costi (food cost).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-stone-200 rounded-sm p-6">
          <h3 className="font-serif text-2xl text-stone-900 mb-5">Aggiungi fatturato</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Mese</label>
              <input data-testid="sales-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                className="mt-1.5 w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Fatturato (€)</label>
              <input data-testid="sales-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="es. 24500"
                className="mt-1.5 w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
            </div>
            <button data-testid="save-sales-btn" onClick={save} disabled={saving}
              className="w-full rounded-full bg-brand text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-brand-hover transition-colors disabled:opacity-60">
              <Plus size={16} /> {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            {sales.length === 0 && <p className="text-sm text-stone-400">Nessuna vendita registrata.</p>}
            {sales.map((s) => {
              const cost = stats?.series.find((x) => x.month === s.month)?.costs || 0;
              const inc = s.amount > 0 ? (cost / s.amount * 100).toFixed(1) : null;
              return (
                <div key={s.id || s.month} data-testid={`sales-row-${s.month}`} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-stone-800">{s.month}</p>
                    <p className={`text-xs ${inc && threshold && parseFloat(inc) > parseFloat(threshold) ? "text-alert font-bold" : "text-stone-400"}`}>Incidenza {inc ? `${inc}%` : "—"}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-stone-900">{euro(s.amount)}</span>
                    <button onClick={() => del(s.id)} className="text-stone-400 hover:text-alert"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia food cost (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="threshold-input" type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="es. 30"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-threshold-btn" onClick={saveThreshold}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Ti avviso quando l'incidenza mensile supera questo valore.</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Destinatari report settimanale</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="recipients-input" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="commercialista@studio.it, ..."
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-recipients-btn" onClick={saveRecipients}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Email separate da virgola. Vuoto = solo il tuo indirizzo.</p>
          </div>

          <div className="mt-6 pt-6 border-t-2 border-brand/30">
            <h3 className="font-serif text-2xl text-stone-900">Soglie e avvisi</h3>
            <p className="text-sm text-stone-500 mt-1">Configura le soglie che generano avvisi in-app ed email (margine, food cost piatti e composti, rincari).</p>
          </div>
          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia margine avvisi (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="margin-threshold-input" type="number" min="0" max="100" value={marginThreshold} onChange={(e) => setMarginThreshold(e.target.value)} placeholder="es. 20"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-margin-threshold-btn" onClick={saveMarginThreshold}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Ricevi un'email (e un avviso in-app) quando un prodotto in vendita scende sotto questo margine dopo un aumento prezzi.</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia food cost piatti (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="recipe-fc-threshold-input" type="number" min="0" max="100" value={recipeFcThreshold} onChange={(e) => setRecipeFcThreshold(e.target.value)} placeholder="es. 35"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-recipe-fc-btn" onClick={saveRecipeFc}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Usata per i badge nel modulo Ricette e per gli avvisi in-app sui piatti fuori soglia.</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia rincaro piatti (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="recipe-rise-threshold-input" type="number" min="0" max="100" value={recipeRiseThreshold} onChange={(e) => setRecipeRiseThreshold(e.target.value)} placeholder="es. 10"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-recipe-rise-btn" onClick={saveRecipeRise}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Avviso in-app quando il costo primario di un piatto aumenta oltre questa percentuale rispetto al mese precedente (default 10%).</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia rincaro acquisti (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="price-rise-threshold-input" type="number" min="0" max="100" value={priceRiseThreshold} onChange={(e) => setPriceRiseThreshold(e.target.value)} placeholder="es. 15"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-price-rise-btn" onClick={savePriceRise}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Avviso in-app e badge in Cambusa quando l'ultimo prezzo d'acquisto di un prodotto supera la sua media storica oltre questa percentuale (default 15%).</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia food cost prodotti composti (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="composite-fc-threshold-input" type="number" min="0" max="100" value={compositeFcThreshold} onChange={(e) => setCompositeFcThreshold(e.target.value)} placeholder="es. 35"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-composite-fc-btn" onClick={saveCompositeFc}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Avviso in-app quando il costo della distinta base di un prodotto composto vendibile supera questa percentuale del prezzo di vendita (default 35%).</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Soglia abbinamento import (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="fuzzy-threshold-input" type="number" min="50" max="100" value={fuzzyThreshold} onChange={(e) => setFuzzyThreshold(e.target.value)} placeholder="es. 86"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-fuzzy-btn" onClick={saveFuzzyThreshold}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Sensibilità dell'abbinamento per descrizione durante l'import fatture (default 86%). Più alta = più severa (meno abbinamenti automatici). Le righe appena sotto soglia diventano suggerimenti da confermare.</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">IVA di default ordini (%)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="default-vat-input" type="number" min="0" max="100" value={defaultVat} onChange={(e) => setDefaultVat(e.target.value)} placeholder="es. 10"
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-default-vat-btn" onClick={saveDefaultVat}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Aliquota IVA applicata nei PDF ordine ai prodotti senza aliquota impostata (default 10%), così lo scorporo IVA è sempre presente.</p>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Giorno avviso previsione budget</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="alert-day-input" type="number" min="1" max="28" value={alertDay} onChange={(e) => setAlertDay(e.target.value)}
                className="flex-1 border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
              <button data-testid="save-alert-day-btn" onClick={saveAlertDay}
                className="rounded-full bg-brand text-white px-5 text-sm font-semibold hover:bg-brand-hover transition-colors">OK</button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">Giorno del mese (1-28) in cui ricevere l'avviso di previsione.</p>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {stats?.incidence_alerts?.length > 0 && (
            <div data-testid="incidence-alert-banner" className="bg-alert/10 border border-alert/30 rounded-sm p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-alert mt-0.5 shrink-0" />
              <div className="text-sm text-stone-700">
                <b className="text-alert">Soglia food cost superata.</b> Mesi oltre il {stats.food_cost_threshold}%: {stats.incidence_alerts.map((a) => `${a.month} (${a.incidence}%)`).join(", ")}
              </div>
            </div>
          )}
          <div data-testid="global-incidence" className="bg-brand text-white rounded-sm p-8 relative overflow-hidden noise-overlay">
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-[0.15em] font-bold mb-3">
              <Percent size={16} /> Incidenza costi complessiva
            </div>
            <div className="font-serif text-6xl font-light">{stats?.food_cost_pct == null ? "—" : `${stats.food_cost_pct}%`}</div>
            <div className="flex gap-8 mt-6 text-sm">
              <div><p className="text-white/60 text-xs">Costi totali</p><p className="font-semibold">{euro(stats?.total_costs)}</p></div>
              <div><p className="text-white/60 text-xs">Vendite totali</p><p className="font-semibold">{euro(stats?.total_sales)}</p></div>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <h3 className="font-serif text-2xl text-stone-900 mb-5">Costi vs Vendite per mese</h3>
            {chartData.length === 0 ? (
              <div className="h-72 flex items-center justify-center text-stone-400 text-sm">Nessun dato da mostrare.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                  <XAxis dataKey="month" stroke="#A8A29E" fontSize={12} />
                  <YAxis stroke="#A8A29E" fontSize={12} />
                  <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7E5E4", fontFamily: "Manrope" }} />
                  <Legend />
                  <Bar dataKey="sales" name="Vendite" fill="#2C4A3B" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="costs" name="Costi" fill="#8B3A3A" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
